import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTheme } from '@mui/material/styles';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { DateRange } from 'react-date-range';
import format from 'date-fns/format';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import { ptBR } from 'date-fns/locale';

import { ReportECFilters as Filters, ReportECUsers } from './reportEC.utils';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

interface Props {
  filters: Filters;
  onChange: (filters: Filters) => void;
  onSearch: () => void;
  onReset: () => void;
  loading: boolean;
  pending: boolean;
  isRestrictedUser: boolean;
  users: ReportECUsers;
  identificationFilters?: boolean;
}

const ReportECFilters = ({
  filters,
  onChange,
  onSearch,
  onReset,
  loading,
  pending,
  isRestrictedUser,
  users,
  identificationFilters = false,
}: Props) => {
  const theme = useTheme();
  const isSmall = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));
  const [calendarOpen, setCalendarOpen] = React.useState(false);
  const [range, setRange] = React.useState({
    startDate: filters.dateStart,
    endDate: filters.dateEnd,
  });
  const userOptions = users.data || [];
  const hasSelectedUser = userOptions.some(
    (item) => String(item.CdUsuario) === filters.userCode
  );
  const selectedUser = filters.userCode
    ? userOptions.find(
        (item) => String(item.CdUsuario) === filters.userCode
      ) || {
        CdUsuario: filters.userCode,
        NmUsuario: 'Usuário atual',
      }
    : { CdUsuario: '', NmUsuario: 'Todos' };
  const selectableUsers = [
    ...(!isRestrictedUser ? [{ CdUsuario: '', NmUsuario: 'Todos' }] : []),
    ...userOptions,
    ...(filters.userCode && !hasSelectedUser ? [selectedUser] : []),
  ];
  const openCalendar = () => {
    setRange({ startDate: filters.dateStart, endDate: filters.dateEnd });
    setCalendarOpen(true);
  };

  return (
    <Paper
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!loading) onSearch();
      }}
      sx={{
        p: { xs: 1.5, md: 2 },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: '0 8px 24px rgba(17, 24, 39, 0.05)',
        '& .MuiOutlinedInput-root': { height: 40 },
        '& .MuiInputBase-input': { fontSize: 14, letterSpacing: 0 },
        '& .MuiInputLabel-root': {
          fontSize: 16,
          fontWeight: 500,
          letterSpacing: 0,
        },
      }}
    >
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={1.5}>
        <FilterListIcon fontSize="small" color="primary" />
        <Typography component="h2" variant="subtitle2" fontWeight={700}>
          Filtros da pesquisa
        </Typography>
        {pending && (
          <Chip
            label="Não aplicados"
            size="small"
            color="warning"
            sx={{ height: 22, fontSize: 11 }}
          />
        )}
      </Box>
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: '1fr',
          sm: '1fr 1fr',
          lg: '1.1fr 1.1fr 1fr 1fr',
        }}
        gap={1.25}
      >
        {[
          { key: 'dateStart', label: 'Data inicial' },
          { key: 'dateEnd', label: 'Data final' },
        ].map(({ key, label }) => (
          <TextField
            key={key}
            id={`report-ec-${key}`}
            label={label}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={format(filters[key], 'dd/MM/yyyy')}
            disabled={loading}
            onClick={openCalendar}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openCalendar();
              }
            }}
            inputProps={{
              'aria-haspopup': 'dialog',
              'aria-expanded': calendarOpen,
            }}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <CalendarTodayIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        ))}
        <TextField
          id="report-ec-payment-type"
          select
          label="Tipo de pagamento"
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
          value={filters.paymentType}
          disabled={loading}
          onChange={(event) =>
            onChange({ ...filters, paymentType: event.target.value })
          }
        >
          <MenuItem value="">Todos</MenuItem>
          {['Débito', 'Crédito', 'PIX', 'Boleto'].map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>
        <Autocomplete
          id="report-ec-user"
          size="small"
          fullWidth
          options={selectableUsers}
          value={selectedUser}
          getOptionLabel={(option) => option.NmUsuario}
          isOptionEqualToValue={(option, value) =>
            String(option.CdUsuario) === String(value.CdUsuario)
          }
          selectOnFocus
          autoHighlight
          disableClearable={!filters.userCode || isRestrictedUser}
          clearText="Limpar usuário"
          openText="Selecionar usuário"
          closeText="Fechar usuários"
          noOptionsText="Nenhum usuário encontrado"
          ListboxProps={{ style: { fontSize: 14, maxHeight: 280 } }}
          componentsProps={{
            clearIndicator: { style: { visibility: 'visible' } },
          }}
          disabled={loading || isRestrictedUser || Boolean(users.loading)}
          onChange={(_event, option) =>
            onChange({
              ...filters,
              userCode: option ? String(option.CdUsuario) : '',
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Usuário"
              placeholder="Busque pelo nome"
              InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
            />
          )}
        />
      </Box>
      {identificationFilters && (
        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
          gap={1.25}
          mt={1.25}
        >
          <TextField
            id="report-ec-client-identification"
            label="Identificação do cliente"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filters.idClientCartorio || ''}
            disabled={loading}
            onChange={(event) =>
              onChange({ ...filters, idClientCartorio: event.target.value })
            }
          />
          <TextField
            id="report-ec-service-identification"
            label="Identificação do serviço"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filters.idServiceCartorio || ''}
            disabled={loading}
            onChange={(event) =>
              onChange({ ...filters, idServiceCartorio: event.target.value })
            }
          />
        </Box>
      )}
      {users.error && !isRestrictedUser && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          Não foi possível carregar os usuários.{' '}
          <Button
            color="inherit"
            size="small"
            onClick={() => users.handleFetch()}
          >
            Tentar novamente
          </Button>
        </Alert>
      )}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1.5}
        mt={1.5}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="caption" color="text.secondary" mr={0.5}>
            Período:
          </Typography>
          {[
            { label: 'Hoje', days: 1 },
            { label: '7 dias', days: 7 },
            { label: '30 dias', days: 30 },
          ].map(({ label, days }) => {
            const end = startOfDay(new Date());
            const start = subDays(end, days - 1);
            const active =
              filters.dateStart.getTime() === start.getTime() &&
              filters.dateEnd.getTime() === end.getTime();
            return (
              <Button
                key={days}
                size="small"
                disabled={loading}
                aria-pressed={active}
                onClick={() =>
                  onChange({ ...filters, dateStart: start, dateEnd: end })
                }
                sx={{
                  minWidth: 0,
                  px: 1,
                  fontSize: 12,
                  bgcolor: active ? 'primary.light3' : 'transparent',
                  fontWeight: 700,
                }}
              >
                {label}
              </Button>
            );
          })}
        </Box>
        <Box display="flex" gap={1}>
          <Button
            size="small"
            onClick={onReset}
            disabled={loading}
            sx={{ minHeight: 40, fontWeight: 700 }}
          >
            Limpar filtros
          </Button>
          <LoadingButton
            type="submit"
            size="small"
            variant="contained"
            startIcon={<SearchIcon />}
            loading={loading}
            sx={{ minHeight: 40, fontWeight: 700 }}
          >
            Pesquisar
          </LoadingButton>
        </Box>
      </Box>
      {pending && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mt={1}
        >
          Clique em Pesquisar para aplicar as alterações e liberar a exportação.
        </Typography>
      )}
      <Dialog
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="report-ec-period-title"
      >
        <DialogTitle id="report-ec-period-title">
          Selecionar período
        </DialogTitle>
        <DialogContent
          sx={{
            p: { xs: 0, sm: 1 },
            '& .rdrCalendarWrapper': { width: '100%' },
            '& .rdrMonth': { width: '100%' },
            '& .rdrMonths': { width: '100%' },
          }}
        >
          <DateRange
            rangeColors={[theme.palette.primary.main]}
            weekdayDisplayFormat="EEEEEE"
            monthDisplayFormat="MMMM yyyy"
            locale={ptBR}
            showDateDisplay={false}
            months={isSmall ? 1 : 2}
            direction={isSmall ? 'vertical' : 'horizontal'}
            ranges={[{ ...range, key: 'selection' }]}
            onChange={({ selection }) =>
              setRange({
                startDate: startOfDay(selection.startDate),
                endDate: startOfDay(selection.endDate),
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalendarOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => {
              onChange({
                ...filters,
                dateStart: range.startDate,
                dateEnd: range.endDate,
              });
              setCalendarOpen(false);
            }}
          >
            Aplicar período
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ReportECFilters;
